import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import adminAPI, {
  AdminUser,
  AdminSubscription,
  AdminPaddleUserSyncPreview,
} from "../admin.api";
import { TextField, MenuItem, FormControlLabel, Checkbox } from "@mui/material";
import PaddleSyncPreviewDialog from "./PaddleSyncPreviewDialog";

interface UserSubscriptionsTabProps {
  user: AdminUser;
}

function UserSubscriptionsTab({ user }: UserSubscriptionsTabProps) {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<AdminSubscription | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addingSubscription, setAddingSubscription] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(false);
  const [editSubscription, setEditSubscription] = useState<Partial<AdminSubscription> | null>(null);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncPreviewLoading, setSyncPreviewLoading] = useState(false);
  const [syncingPaddle, setSyncingPaddle] = useState(false);
  const [syncPreview, setSyncPreview] = useState<AdminPaddleUserSyncPreview | null>(null);
  const [syncPreviewError, setSyncPreviewError] = useState<string | null>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [newSubscription, setNewSubscription] = useState({
    processor: "manual",
    status: "active",
    planId: "",
    startDate: new Date().toISOString().split('T')[0],
    rebillDate: "",
    endDate: "",
    autoRenew: false,
    features: {
      AI_IMAGE_USAGE: { used: 0, limit: 300 },
      KEYWORD_TOOL: { enabled: true },
      NICHE_TOOL: { enabled: true },
      HEADLINE_TOOL: { enabled: true },
      KEYWORD_TOOL_UNLIMITED: { enabled: false },
      NICHE_TOOL_UNLIMITED: { enabled: false },
      HEADLINE_TOOL_UNLIMITED: { enabled: false }
    }
  });

  const resetSyncDialog = () => {
    setSyncDialogOpen(false);
    setSyncPreview(null);
    setSyncPreviewError(null);
    setSyncPreviewLoading(false);
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getUserSubscriptions(user._id);
      setSubscriptions(response?.subscriptions || []);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || "Failed to fetch subscriptions");
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await adminAPI.getPlans();
      setPlans(response?.plans || []);
    } catch (err: unknown) {
      console.error("Failed to fetch plans:", err);
      setPlans([]);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchPlans();
  }, [user._id]);

  const handleDeleteClick = (subscription: AdminSubscription) => {
    setSelectedSubscription(subscription);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubscription) return;
    
    setError(null);
    try {
      await adminAPI.deleteSubscription(selectedSubscription._id);
      fetchSubscriptions();
      setDeleteDialogOpen(false);
      setSelectedSubscription(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || "Failed to delete subscription");
    }
  };

  const handleAddSubscription = async () => {
    setAddingSubscription(true);
    setError(null);
    try {
      await adminAPI.createSubscription(user._id, newSubscription);
      fetchSubscriptions();
      setAddDialogOpen(false);
      // Reset form
      setNewSubscription({
        processor: "manual",
        status: "active",
        planId: "",
        startDate: new Date().toISOString().split('T')[0],
        rebillDate: "",
        endDate: "",
        autoRenew: false,
        features: {
          AI_IMAGE_USAGE: { used: 0, limit: 300 },
          KEYWORD_TOOL: { enabled: true },
          NICHE_TOOL: { enabled: true },
          HEADLINE_TOOL: { enabled: true },
          KEYWORD_TOOL_UNLIMITED: { enabled: false },
          NICHE_TOOL_UNLIMITED: { enabled: false },
          HEADLINE_TOOL_UNLIMITED: { enabled: false }
        }
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || "Failed to create subscription");
    } finally {
      setAddingSubscription(false);
    }
  };

  const handleEditClick = (subscription: AdminSubscription) => {
    const formatDate = (date: string | undefined) => {
      if (!date) return "";
      return new Date(date).toISOString().split('T')[0];
    };

    setEditSubscription({
      _id: subscription._id,
      processor: subscription.processor,
      status: subscription.status,
      planId: subscription.planId?._id || subscription.planId || "",
      startDate: formatDate(subscription.startDate),
      rebillDate: formatDate(subscription.rebillDate),
      endDate: formatDate(subscription.endDate),
      autoRenew: subscription.autoRenew,
      paddleSubscriptionId: subscription.paddleSubscriptionId || "",
      features: {
        AI_IMAGE_USAGE: subscription.features?.AI_IMAGE_USAGE || { used: 0, limit: 300 },
        KEYWORD_TOOL: subscription.features?.KEYWORD_TOOL || { enabled: true },
        NICHE_TOOL: subscription.features?.NICHE_TOOL || { enabled: true },
        HEADLINE_TOOL: subscription.features?.HEADLINE_TOOL || { enabled: true },
        KEYWORD_TOOL_UNLIMITED: subscription.features?.KEYWORD_TOOL_UNLIMITED || { enabled: false },
        NICHE_TOOL_UNLIMITED: subscription.features?.NICHE_TOOL_UNLIMITED || { enabled: false },
        HEADLINE_TOOL_UNLIMITED: subscription.features?.HEADLINE_TOOL_UNLIMITED || { enabled: false }
      }
    });
    setEditDialogOpen(true);
  };

  const handleSyncPreviewClick = async () => {
    setSyncPreview(null);
    setSyncPreviewError(null);
    setSyncDialogOpen(true);
    setSyncPreviewLoading(true);

    try {
      const response = await adminAPI.getPaddleUserSyncPreview(user._id);
      setSyncPreview(response?.preview || null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setSyncPreviewError(error.response?.data?.error?.message || "Failed to load Paddle sync preview");
    } finally {
      setSyncPreviewLoading(false);
    }
  };

  const handleCloseSyncDialog = () => {
    if (syncingPaddle) return;
    resetSyncDialog();
  };

  const handleConfirmPaddleSync = async () => {
    setSyncingPaddle(true);
    setSyncPreviewError(null);

    try {
      await adminAPI.syncPaddleUser(user._id);
      await fetchSubscriptions();
      resetSyncDialog();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setSyncPreviewError(error.response?.data?.error?.message || "Failed to sync user subscriptions with Paddle");
    } finally {
      setSyncingPaddle(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!editSubscription || !editSubscription._id) return;
    
    setEditingSubscription(true);
    setError(null);
    try {
      const { _id, ...updates } = editSubscription;
      // Convert empty date strings to undefined
      if (!updates.rebillDate) updates.rebillDate = undefined;
      if (!updates.endDate) updates.endDate = undefined;
      if (!updates.planId) updates.planId = undefined;
      
      await adminAPI.updateSubscription(_id, updates);
      fetchSubscriptions();
      setEditDialogOpen(false);
      setEditSubscription(null);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || "Failed to update subscription");
    } finally {
      setEditingSubscription(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "trial":
      case "trialing":
        return "info";
      case "cancelled":
        return "warning";
      case "paused":
      case "expired":
      case "payment-failed":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString();
  };

  const canSyncWithPaddle = Boolean(user.email || user.paddleId);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canSyncWithPaddle && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<SyncIcon />}
              onClick={() => handleSyncPreviewClick()}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3
              }}
            >
              Sync User With Paddle
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Add Subscription
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {subscriptions.length === 0 ? (
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            textAlign: "center",
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}
        >
          <Typography color="textSecondary">
            No subscriptions found for this user
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 3,
              overflowX: 'auto',
              '& .MuiTable-root': {
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: 1200,
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  bgcolor: 'background.paper',
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    py: 2,
                  }
                }}>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Processor</TableCell>
                  <TableCell>Paddle ID</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Rebill Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="center">Auto Renew</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((subscription, index) => (
                  <TableRow 
                    key={subscription._id}
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      '& .MuiTableCell-body': {
                        borderBottom: index === subscriptions.length - 1 ? 'none' : '1px solid',
                        borderColor: 'divider',
                        py: 2,
                        fontSize: '0.875rem',
                      },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell>
                      <Tooltip title={`Subscription ID: ${subscription._id}`} placement="top" arrow>
                        <Typography variant="body2" fontWeight={600}>
                          {subscription.planId?.name || subscription.planId || "Unknown"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscription.status}
                        color={getStatusColor(subscription.status) as "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"}
                        size="small"
                        sx={{ 
                          fontWeight: 600,
                          borderRadius: 1.5,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={subscription.processor}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {subscription.paddleSubscriptionId ? (
                        <code style={{ fontSize: '0.75rem' }}>
                          {subscription.paddleSubscriptionId}
                        </code>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {subscription.features?.KEYWORD_TOOL?.enabled && (
                          <Chip 
                            label={subscription.features?.KEYWORD_TOOL_UNLIMITED?.enabled ? "Keyword ∞" : "Keyword"} 
                            size="small" 
                            color={subscription.features?.KEYWORD_TOOL_UNLIMITED?.enabled ? "success" : "primary"} 
                            variant={subscription.features?.KEYWORD_TOOL_UNLIMITED?.enabled ? "filled" : "outlined"} 
                            sx={{ fontSize: '0.7rem' }} 
                          />
                        )}
                        {subscription.features?.NICHE_TOOL?.enabled && (
                          <Chip 
                            label={subscription.features?.NICHE_TOOL_UNLIMITED?.enabled ? "Niche ∞" : "Niche"} 
                            size="small" 
                            color={subscription.features?.NICHE_TOOL_UNLIMITED?.enabled ? "success" : "primary"} 
                            variant={subscription.features?.NICHE_TOOL_UNLIMITED?.enabled ? "filled" : "outlined"} 
                            sx={{ fontSize: '0.7rem' }} 
                          />
                        )}
                        {subscription.features?.HEADLINE_TOOL?.enabled && (
                          <Chip 
                            label={subscription.features?.HEADLINE_TOOL_UNLIMITED?.enabled ? "Headline ∞" : "Headline"} 
                            size="small" 
                            color={subscription.features?.HEADLINE_TOOL_UNLIMITED?.enabled ? "success" : "primary"} 
                            variant={subscription.features?.HEADLINE_TOOL_UNLIMITED?.enabled ? "filled" : "outlined"} 
                            sx={{ fontSize: '0.7rem' }} 
                          />
                        )}
                        {subscription.features?.AI_IMAGE_USAGE?.limit && (
                          <Chip label={`Images: ${subscription.features.AI_IMAGE_USAGE.limit}`} size="small" color="secondary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {formatDate(subscription.startDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {subscription.rebillDate ? (
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(subscription.rebillDate)}
                          </Typography>
                          {new Date(subscription.rebillDate) < new Date() && (
                            <Chip 
                              label="Overdue" 
                              size="small" 
                              color="warning" 
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {subscription.endDate ? (
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(subscription.endDate)}
                          </Typography>
                          {new Date(subscription.endDate) < new Date() && (
                            <Chip 
                              label="Ended" 
                              size="small" 
                              color="error" 
                              sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box 
                        sx={{ 
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          bgcolor: subscription.autoRenew ? 'success.lighter' : 'grey.100',
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          fontWeight={600}
                          color={subscription.autoRenew ? "success.main" : "text.secondary"}
                        >
                          {subscription.autoRenew ? "✓" : "✗"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(subscription)}
                          title="Edit Subscription"
                          aria-label="Edit subscription"
                          sx={{
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.lighter',
                            },
                            transition: 'all 0.2s',
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(subscription)}
                          title="Delete Subscription"
                          aria-label="Delete subscription"
                          sx={{
                            color: 'text.secondary',
                            '&:hover': {
                              color: 'error.main',
                              bgcolor: 'error.lighter',
                            },
                            transition: 'all 0.2s',
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this subscription? This action cannot be undone.
            {selectedSubscription && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Plan:</strong> {selectedSubscription.planId?.name || selectedSubscription.planId || "Unknown"}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedSubscription.status}
                </Typography>
                <Typography variant="body2">
                  <strong>Processor:</strong> {selectedSubscription.processor}
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PaddleSyncPreviewDialog
        open={syncDialogOpen}
        preview={syncPreview}
        loading={syncPreviewLoading}
        syncing={syncingPaddle}
        error={syncPreviewError}
        user={user}
        subscriptions={subscriptions}
        onClose={handleCloseSyncDialog}
        onConfirm={handleConfirmPaddleSync}
      />

      {/* Add Subscription Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon color="success" />
            <Typography variant="h6">Add Subscription</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Plan"
              value={newSubscription.planId}
              onChange={(e) => setNewSubscription(prev => ({ ...prev, planId: e.target.value }))}
              fullWidth
              helperText="Select a plan to associate with this subscription"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="">No Plan</MenuItem>
              {plans.map((plan) => (
                <MenuItem key={plan._id} value={plan._id}>
                  {plan.name} - ${plan.pricingDetails?.price || 0}/{plan.pricingDetails?.intervalLabel || 'month'}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Processor"
              value={newSubscription.processor}
              onChange={(e) => setNewSubscription(prev => ({ ...prev, processor: e.target.value }))}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="paddle">Paddle</MenuItem>
              <MenuItem value="paykickstart">PayKickstart</MenuItem>
              <MenuItem value="clickbank">Clickbank</MenuItem>
              <MenuItem value="recurly">Recurly</MenuItem>
            </TextField>

            <TextField
              select
              label="Status"
              value={newSubscription.status}
              onChange={(e) => setNewSubscription(prev => ({ ...prev, status: e.target.value }))}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="paused">Paused</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </TextField>

            <TextField
              label="Start Date"
              type="date"
              value={newSubscription.startDate}
              onChange={(e) => setNewSubscription(prev => ({ ...prev, startDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <TextField
              label="Rebill Date (Optional)"
              type="date"
              value={newSubscription.rebillDate}
              onChange={(e) => setNewSubscription(prev => ({ ...prev, rebillDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <TextField
              label="End Date (Optional)"
              type="date"
              value={newSubscription.endDate}
              onChange={(e) => setNewSubscription(prev => ({ ...prev, endDate: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <TextField
              label="Image Credit Limit"
              type="number"
              value={newSubscription.features.AI_IMAGE_USAGE.limit}
              onChange={(e) => setNewSubscription(prev => ({ 
                ...prev, 
                features: { 
                  ...prev.features,
                  AI_IMAGE_USAGE: { 
                    ...prev.features.AI_IMAGE_USAGE,
                    limit: parseInt(e.target.value) || 0 
                  }
                }
              }))}
              fullWidth
              helperText="Monthly image generation credits"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={newSubscription.autoRenew}
                  onChange={(e) => setNewSubscription(prev => ({ ...prev, autoRenew: e.target.checked }))}
                />
              }
              label="Auto Renew"
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Feature Toggles</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSubscription.features.KEYWORD_TOOL?.enabled ?? true}
                      onChange={(e) => setNewSubscription(prev => ({ 
                        ...prev, 
                        features: { 
                          ...prev.features,
                          KEYWORD_TOOL: { enabled: e.target.checked }
                        }
                      }))}
                    />
                  }
                  label="Keyword Tool"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSubscription.features.NICHE_TOOL?.enabled ?? true}
                      onChange={(e) => setNewSubscription(prev => ({ 
                        ...prev, 
                        features: { 
                          ...prev.features,
                          NICHE_TOOL: { enabled: e.target.checked }
                        }
                      }))}
                    />
                  }
                  label="Niche Tool"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSubscription.features.HEADLINE_TOOL?.enabled ?? true}
                      onChange={(e) => setNewSubscription(prev => ({ 
                        ...prev, 
                        features: { 
                          ...prev.features,
                          HEADLINE_TOOL: { enabled: e.target.checked }
                        }
                      }))}
                    />
                  }
                  label="Headline Tool"
                />
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 600 }}>Unlimited Access</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSubscription.features.KEYWORD_TOOL_UNLIMITED?.enabled ?? false}
                      onChange={(e) => setNewSubscription(prev => ({ 
                        ...prev, 
                        features: { 
                          ...prev.features,
                          KEYWORD_TOOL_UNLIMITED: { enabled: e.target.checked }
                        }
                      }))}
                    />
                  }
                  label="Keyword Tool Unlimited"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSubscription.features.NICHE_TOOL_UNLIMITED?.enabled ?? false}
                      onChange={(e) => setNewSubscription(prev => ({ 
                        ...prev, 
                        features: { 
                          ...prev.features,
                          NICHE_TOOL_UNLIMITED: { enabled: e.target.checked }
                        }
                      }))}
                    />
                  }
                  label="Niche Tool Unlimited"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newSubscription.features.HEADLINE_TOOL_UNLIMITED?.enabled ?? false}
                      onChange={(e) => setNewSubscription(prev => ({ 
                        ...prev, 
                        features: { 
                          ...prev.features,
                          HEADLINE_TOOL_UNLIMITED: { enabled: e.target.checked }
                        }
                      }))}
                    />
                  }
                  label="Headline Tool Unlimited"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setAddDialogOpen(false)}
            disabled={addingSubscription}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddSubscription}
            disabled={addingSubscription || !newSubscription.startDate}
            startIcon={addingSubscription ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingSubscription ? "Creating..." : "Create Subscription"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Subscription Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditIcon color="primary" />
            <Typography variant="h6">Edit Subscription</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editSubscription && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Plan"
                value={editSubscription.planId}
                onChange={(e) => setEditSubscription(prev => ({ ...prev, planId: e.target.value }))}
                fullWidth
                helperText="Select a plan to associate with this subscription"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="">No Plan</MenuItem>
                {plans.map((plan) => (
                  <MenuItem key={plan._id} value={plan._id}>
                    {plan.name} - ${plan.pricingDetails?.price || 0}/{plan.pricingDetails?.intervalLabel || 'month'}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Processor"
                value={editSubscription.processor}
                onChange={(e) => setEditSubscription(prev => ({ ...prev, processor: e.target.value }))}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="paddle">Paddle</MenuItem>
                <MenuItem value="paykickstart">PayKickstart</MenuItem>
                <MenuItem value="clickbank">Clickbank</MenuItem>
                <MenuItem value="recurly">Recurly</MenuItem>
              </TextField>

              <TextField
                label="Paddle Subscription ID"
                value={editSubscription.paddleSubscriptionId || ""}
                onChange={(e) => setEditSubscription(prev => prev ? ({ ...prev, paddleSubscriptionId: e.target.value }) : null)}
                fullWidth
                placeholder="sub_01abc123..."
                helperText="The Paddle subscription ID (e.g., sub_01abc123...)"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                select
                label="Status"
                value={editSubscription.status}
                onChange={(e) => setEditSubscription(prev => ({ ...prev, status: e.target.value }))}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </TextField>

              <TextField
                label="Start Date"
                type="date"
                value={editSubscription.startDate}
                onChange={(e) => setEditSubscription(prev => ({ ...prev, startDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                label="Rebill Date (Optional)"
                type="date"
                value={editSubscription.rebillDate}
                onChange={(e) => setEditSubscription(prev => ({ ...prev, rebillDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                label="End Date (Optional)"
                type="date"
                value={editSubscription.endDate}
                onChange={(e) => setEditSubscription(prev => ({ ...prev, endDate: e.target.value }))}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                label="Image Credit Limit"
                type="number"
                value={editSubscription.features?.AI_IMAGE_USAGE?.limit || 0}
                onChange={(e) => setEditSubscription(prev => prev ? ({ 
                  ...prev, 
                  features: { 
                    ...prev.features,
                    AI_IMAGE_USAGE: { 
                      ...prev.features?.AI_IMAGE_USAGE,
                      limit: parseInt(e.target.value) || 0 
                    }
                  }
                }) : null)}
                fullWidth
                helperText="Monthly image generation credits"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <TextField
                label="Image Credits Used"
                type="number"
                value={editSubscription.features?.AI_IMAGE_USAGE?.used || 0}
                onChange={(e) => setEditSubscription(prev => prev ? ({ 
                  ...prev, 
                  features: { 
                    ...prev.features,
                    AI_IMAGE_USAGE: { 
                      ...prev.features?.AI_IMAGE_USAGE,
                      used: parseInt(e.target.value) || 0 
                    }
                  }
                }) : null)}
                fullWidth
                helperText="Number of credits already used"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={editSubscription.autoRenew}
                    onChange={(e) => setEditSubscription(prev => prev ? ({ ...prev, autoRenew: e.target.checked }) : null)}
                  />
                }
                label="Auto Renew"
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Feature Toggles</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSubscription.features?.KEYWORD_TOOL?.enabled ?? true}
                        onChange={(e) => setEditSubscription(prev => prev ? ({ 
                          ...prev, 
                          features: { 
                            ...prev.features,
                            KEYWORD_TOOL: { enabled: e.target.checked }
                          }
                        }) : null)}
                      />
                    }
                    label="Keyword Tool"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSubscription.features?.NICHE_TOOL?.enabled ?? true}
                        onChange={(e) => setEditSubscription(prev => prev ? ({ 
                          ...prev, 
                          features: { 
                            ...prev.features,
                            NICHE_TOOL: { enabled: e.target.checked }
                          }
                        }) : null)}
                      />
                    }
                    label="Niche Tool"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSubscription.features?.HEADLINE_TOOL?.enabled ?? true}
                        onChange={(e) => setEditSubscription(prev => prev ? ({ 
                          ...prev, 
                          features: { 
                            ...prev.features,
                            HEADLINE_TOOL: { enabled: e.target.checked }
                          }
                        }) : null)}
                      />
                    }
                    label="Headline Tool"
                  />
                </Box>
                
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontWeight: 600 }}>Unlimited Access</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSubscription.features?.KEYWORD_TOOL_UNLIMITED?.enabled ?? false}
                        onChange={(e) => setEditSubscription(prev => prev ? ({ 
                          ...prev, 
                          features: { 
                            ...prev.features,
                            KEYWORD_TOOL_UNLIMITED: { enabled: e.target.checked }
                          }
                        }) : null)}
                      />
                    }
                    label="Keyword Tool Unlimited"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSubscription.features?.NICHE_TOOL_UNLIMITED?.enabled ?? false}
                        onChange={(e) => setEditSubscription(prev => prev ? ({ 
                          ...prev, 
                          features: { 
                            ...prev.features,
                            NICHE_TOOL_UNLIMITED: { enabled: e.target.checked }
                          }
                        }) : null)}
                      />
                    }
                    label="Niche Tool Unlimited"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editSubscription.features?.HEADLINE_TOOL_UNLIMITED?.enabled ?? false}
                        onChange={(e) => setEditSubscription(prev => prev ? ({ 
                          ...prev, 
                          features: { 
                            ...prev.features,
                            HEADLINE_TOOL_UNLIMITED: { enabled: e.target.checked }
                          }
                        }) : null)}
                      />
                    }
                    label="Headline Tool Unlimited"
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setEditDialogOpen(false);
              setEditSubscription(null);
            }}
            disabled={editingSubscription}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateSubscription}
            disabled={editingSubscription || !editSubscription?.startDate}
            startIcon={editingSubscription ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {editingSubscription ? "Updating..." : "Update Subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserSubscriptionsTab;
